<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { loadingMessages } from '../../messages';

const props = withDefaults(
  defineProps<{
    lines?: number;
    fill?: boolean;
  }>(),
  {
    lines: 3,
    fill: false,
  },
);

const skeletonElement = ref<HTMLElement | null>(null);
const barsThatFit = ref(0);

const widths = ['70%', '92%', '55%', '84%', '63%'];
const bars = computed(() =>
  Array.from(
    { length: Math.max(1, props.lines, barsThatFit.value) },
    (_, index) => ({
      key: index,
      width: widths[index % widths.length],
    }),
  ),
);

const countBarsThatFit = () => {
  const element = skeletonElement.value;
  const firstBar = element?.querySelector<HTMLElement>('.itp-skeleton__bar');
  if (!element || !firstBar) {
    return;
  }

  const styles = getComputedStyle(element);
  const gap = parseFloat(styles.rowGap) || 0;
  const barHeight = firstBar.offsetHeight;
  const available =
    element.clientHeight -
    (parseFloat(styles.paddingTop) || 0) -
    (parseFloat(styles.paddingBottom) || 0);

  barsThatFit.value =
    barHeight > 0 ? Math.floor((available + gap) / (barHeight + gap)) : 0;
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (
    !props.fill ||
    !skeletonElement.value ||
    typeof ResizeObserver === 'undefined'
  ) {
    return;
  }
  countBarsThatFit();
  resizeObserver = new ResizeObserver(countBarsThatFit);
  resizeObserver.observe(skeletonElement.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <div
    ref="skeletonElement"
    class="itp-skeleton"
    :class="{ 'itp-skeleton--fill': fill }"
    role="status"
    aria-live="polite"
  >
    <span class="visually-hidden">{{ loadingMessages.loading }}</span>
    <span
      v-for="bar in bars"
      :key="bar.key"
      class="itp-skeleton__bar"
      :style="{ width: bar.width }"
      aria-hidden="true"
    />
  </div>
</template>
